import AppHeader from '@components/AppNavigation/AppHeader';
import { ActionsFooter } from '@components/Forms';
import ApprovalDialogButton from '@components/Forms/ApprovalDialog/ApprovalDialogButton';
import IntervalSelect from '@components/IntervalSelect/IntervalSelect';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import { LoadingButton } from '@mui/lab';
import { Box, Grid } from '@mui/material';
import { onSubmitForm } from '@utils/forms';
import { numericIntervalValidationScheme } from '@utils/validation';
import { AuthMethodRoute } from 'constants/routes';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import { MessageConnectorContext } from 'hooks/useConnectorsContext';
import { omit } from 'lodash';
import React, { FC, useContext, useMemo } from 'react';
import { TimeUnit } from 'types/common';
import {
	ConnectorTypeEnum,
	CUSTOM_TEMPLATE_PROPS,
	IConnectorMethods,
	IConnectors,
	IExtendedForm,
	ProviderPrefix
} from 'types/connectors';
import { IOTPSettings } from 'types/settings';
import { getCustomTemplateVals } from 'utils/connectors';
import { object } from 'yup';
import AuthMethodSwitch from './AuthMethodSwitch';
import { FORM_KEYS as GENERIC_FORM_KEYS } from './CommonFormKeys';
import ConnectorForm from './ConnectorForm/ConnectorForm';

interface IOTPPageProps extends IConnectorMethods<IOTPSettings> {
	settings?: IOTPSettings;
}

const FORM_KEYS = { ...GENERIC_FORM_KEYS, ...{ domain: 'domain' } };

const DEFAULT_FORM: IOTPSettings = {
	...Object.keys(FORM_KEYS).reduce(
		(prev, current) => ({ ...prev, [current]: '' }),
		{}
	),
	enabled: false,
	expirationTimeUnit: TimeUnit.minutes,
	expirationTime: 3
} as IOTPSettings;

const OTPPage: FC<IOTPPageProps> = ({
	settings,
	templates,
	onSaveConnectors,
	onSaveSettings
}) => {
	const { addToast } = useToast();

	const { getCustomTemplates, updateCustomTemplates, setConnectors } =
		useContext(MessageConnectorContext);

	const emailCustomTemplates = getCustomTemplates(
		ConnectorTypeEnum.EMAIL_PROVIDERS
	);

	const smsCustomTemplates = getCustomTemplates(
		ConnectorTypeEnum.SMS_PROVIDERS
	);

	const values = useMemo<IOTPSettings>(
		() => ({
			...DEFAULT_FORM,
			...settings,
			...{
				customEmailBody: emailCustomTemplates?.emailBody || '',
				customEmailSubject: emailCustomTemplates?.emailSubject || '',
				customTextMessageBody: smsCustomTemplates?.textMessageBody || ''
			}
		}),
		[settings, emailCustomTemplates, smsCustomTemplates]
	);

	const formik = useFormik<IExtendedForm<IOTPSettings>>({
		initialValues: values,
		enableReinitialize: true,
		validationSchema: object({
			[FORM_KEYS.expirationTime]: numericIntervalValidationScheme()
		}),
		onSubmit: (vals) => {
			const { customEmailBody, customEmailSubject, customTextMessageBody } =
				vals;
			onSubmitForm(onSaveConnectors)(
				updateCustomTemplates({
					customEmailBody,
					customEmailSubject,
					customTextMessageBody
				})
			).then((connectors) => {
				setConnectors(connectors as IConnectors);
				onSubmitForm(
					onSaveSettings,
					addToast
				)(
					omit(
						getCustomTemplateVals(vals),
						CUSTOM_TEMPLATE_PROPS
					) as IOTPSettings
				);
			});
		}
	});

	return (
		<Box
			display="flex"
			flexGrow={1}
			flexDirection="column"
			data-testid="otp-page"
			component="form"
			overflow="hidden"
		>
			<AppHeader translation="pages.authMethods.otp.title" mb={0} />

			<Box
				flexGrow={1}
				flexDirection="column"
				id="otp-container"
				overflow="auto"
				p={4}
				pt={0}
			>
				<Grid container mb={2}>
					<AuthMethodSwitch formik={formik} />
				</Grid>

				<Grid item container spacing={3} xs={8}>
					<Grid item xs={12}>
						<TextFieldValidation
							id={FORM_KEYS.domain}
							formik={formik}
							fullWidth
							label={intl.tr(`authMethods.fields.${FORM_KEYS.domain}`)}
							placeholder={intl.tr(
								`authMethods.fields.${FORM_KEYS.domain}.placeholder`
							)}
						/>
					</Grid>
					<Grid item container spacing={3}>
						<Grid item xs={6}>
							<TextFieldValidation
								id={FORM_KEYS.expirationTime}
								formik={formik}
								required
								fullWidth
								label={intl.tr(
									`authMethods.fields.${FORM_KEYS.expirationTime}`
								)}
								type="number"
								InputProps={{ inputProps: { min: 1 } }}
							/>
						</Grid>
						<Grid item xs={6}>
							<IntervalSelect
								options={[TimeUnit.minutes, TimeUnit.hours]}
								initialValue={formik.values.expirationTimeUnit}
								onChange={formik.handleChange}
								name={FORM_KEYS.expirationTimeUnit}
							/>
						</Grid>
					</Grid>
				</Grid>
				<ConnectorForm
					providerType={ProviderPrefix.SMS}
					pageId={AuthMethodRoute.OTP}
					formik={formik}
					templates={templates}
				/>
				<ConnectorForm
					providerType={ProviderPrefix.EMAIL}
					pageId={AuthMethodRoute.OTP}
					formik={formik}
					templates={templates}
				/>
			</Box>
			<ActionsFooter>
				<LoadingButton
					color="primary"
					variant="contained"
					loading={formik.isSubmitting}
					disabled={!formik.dirty}
					sx={{ marginRight: 1 }}
					onClick={() => formik.handleSubmit()}
				>
					{intl.tr('button.save')}
				</LoadingButton>
				<ApprovalDialogButton
					buttonText={intl.tr('button.cancel')}
					onApprove={formik.resetForm}
					disabled={!formik.dirty}
					title={intl.tr('discard.changes.dialog.title')}
					question={intl.tr('discard.changes.dialog.question')}
					approveText={intl.tr('discard.changes.dialog.approve')}
				/>
			</ActionsFooter>
		</Box>
	);
};

OTPPage.defaultProps = {
	settings: undefined
};

export default OTPPage;
